import { LICEO_HOME, isSchoolPath, withoutSchoolPrefix } from "@/lib/school-routes";
import type { OfferMode, UserIntentSegment } from "./user-intent";

export const PAYWALL_RECOVERY_TTL_MS = 15 * 60 * 1000;
export const UPGRADE_OBJECTION_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

const PENDING_RECOVERY_KEY = "chickytutor:paywall-recovery:pending";
const RECOVERY_SESSION_KEY_PREFIX = "chickytutor:paywall-recovery:seen:";
const OBJECTION_SESSION_KEY_PREFIX = "chickytutor:upgrade-objections:session:";
const OBJECTION_COOLDOWN_KEY_PREFIX = "chickytutor:upgrade-objections:cooldown:";

export type PaywallExitMethod = "back_button" | "inferred_navigation";
export type UpgradeObjectionReason = "cost" | "need_time" | "unsure_value" | "checkout_problem";

export interface PaywallRecoveryRecord {
  version: 2;
  id: string;
  ownerUserId: string;
  source: string;
  returnTo: string;
  userIntentSegment: UserIntentSegment;
  offerMode: OfferMode;
  couponApplied: boolean;
  enteredAt: number;
  exitMethod: PaywallExitMethod;
  exitEventTracked: boolean;
}

function getSessionStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function getLocalStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function normalizeSource(source: string | null | undefined): string {
  const normalized = (source || "direct")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_")
    .slice(0, 80);
  return normalized || "direct";
}

export function sanitizeInternalReturnTo(value: string | null | undefined): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/";

  try {
    const parsed = new URL(value, "https://chickytutor.invalid");
    if (parsed.origin !== "https://chickytutor.invalid") return "/";

    const pathname = parsed.pathname.replace(/\/{2,}/g, "/");
    const gatePath = withoutSchoolPrefix(pathname);
    const isUnsafeDestination =
      gatePath === "/paywall" ||
      gatePath.startsWith("/paywall/") ||
      gatePath === "/auth" ||
      gatePath.startsWith("/auth/") ||
      gatePath === "/success" ||
      gatePath.startsWith("/success/") ||
      gatePath === "/activate-purchase" ||
      gatePath.startsWith("/activate-purchase/");

    if (isUnsafeDestination) return isSchoolPath(pathname) ? LICEO_HOME : "/";
    return `${pathname}${parsed.search}${parsed.hash}` || "/";
  } catch {
    return "/";
  }
}

export function getBrowserReturnTo(): string {
  if (typeof window === "undefined") return "/";
  return sanitizeInternalReturnTo(
    `${window.location.pathname}${window.location.search}${window.location.hash}`
  );
}

export function getReturnRouteCategory(returnTo: string): string {
  if (isSchoolPath(returnTo)) return "school";
  const pathname = sanitizeInternalReturnTo(returnTo).split(/[?#]/, 1)[0];
  if (pathname === "/") return "home";
  if (pathname.startsWith("/subscription")) return "subscription";
  if (pathname.startsWith("/app/school") || pathname.startsWith("/tutor")) return "tutors";
  if (pathname === "/school" || pathname.startsWith("/school/")) return "school";
  if (pathname === "/schools" || pathname.startsWith("/schools/")) return "schools";
  if (pathname.startsWith("/journey") || pathname.startsWith("/course")) return "journey";
  if (pathname.startsWith("/conversation") || pathname.startsWith("/practice")) return "practice";
  if (pathname.startsWith("/blog") || pathname.startsWith("/tools")) return "content";
  return "other";
}

export function isPaywallRecoveryRouteEligible(pathname: string, source: string): boolean {
  pathname = withoutSchoolPrefix(pathname);
  if (normalizeSource(source) === "mobile_app") return false;

  return ![
    "/paywall",
    "/auth",
    "/success",
    "/activate-purchase",
    "/lifetime",
    "/mobileapp",
    "/reel",
    "/affiliate",
  ].some((excluded) => pathname === excluded || pathname.startsWith(`${excluded}/`));
}

function createRecoveryId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `recovery_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function createPaywallRecoveryRecord({
  couponApplied,
  offerMode,
  ownerUserId,
  returnTo,
  source,
  userIntentSegment,
  now = Date.now(),
}: {
  couponApplied: boolean;
  offerMode: OfferMode;
  ownerUserId: string;
  returnTo?: string | null;
  source?: string | null;
  userIntentSegment: UserIntentSegment;
  now?: number;
}): PaywallRecoveryRecord {
  return {
    version: 2,
    id: createRecoveryId(),
    ownerUserId,
    source: normalizeSource(source),
    returnTo: sanitizeInternalReturnTo(returnTo),
    userIntentSegment,
    offerMode,
    couponApplied,
    enteredAt: now,
    exitMethod: "inferred_navigation",
    exitEventTracked: false,
  };
}

function isPaywallRecoveryRecord(value: unknown): value is PaywallRecoveryRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<PaywallRecoveryRecord>;
  return (
    record.version === 2 &&
    typeof record.id === "string" &&
    typeof record.ownerUserId === "string" &&
    record.ownerUserId.length > 0 &&
    typeof record.source === "string" &&
    typeof record.returnTo === "string" &&
    (record.userIntentSegment === "cold_user" ||
      record.userIntentSegment === "high_intent_returner") &&
    (record.offerMode === "trial" || record.offerMode === "discount") &&
    typeof record.couponApplied === "boolean" &&
    typeof record.enteredAt === "number" &&
    (record.exitMethod === "back_button" || record.exitMethod === "inferred_navigation") &&
    typeof record.exitEventTracked === "boolean"
  );
}

export function writePendingPaywallRecovery(record: PaywallRecoveryRecord): void {
  getSessionStorage()?.setItem(PENDING_RECOVERY_KEY, JSON.stringify(record));
}

export function readPendingPaywallRecovery(now = Date.now()): PaywallRecoveryRecord | null {
  const storage = getSessionStorage();
  const raw = storage?.getItem(PENDING_RECOVERY_KEY);
  if (!storage || !raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isPaywallRecoveryRecord(parsed) || now - parsed.enteredAt > PAYWALL_RECOVERY_TTL_MS) {
      storage.removeItem(PENDING_RECOVERY_KEY);
      return null;
    }
    return { ...parsed, returnTo: sanitizeInternalReturnTo(parsed.returnTo) };
  } catch {
    storage.removeItem(PENDING_RECOVERY_KEY);
    return null;
  }
}

export function updatePendingPaywallRecovery(
  updates: Partial<
    Pick<PaywallRecoveryRecord, "couponApplied" | "exitEventTracked" | "exitMethod" | "offerMode">
  >
): PaywallRecoveryRecord | null {
  const current = readPendingPaywallRecovery();
  if (!current) return null;
  const next = { ...current, ...updates };
  writePendingPaywallRecovery(next);
  return next;
}

export function clearPendingPaywallRecovery(): void {
  getSessionStorage()?.removeItem(PENDING_RECOVERY_KEY);
}

function userStorageKey(prefix: string, userId: string): string {
  return `${prefix}${userId}`;
}

export function hasSeenPaywallRecoveryThisSession(userId: string): boolean {
  return getSessionStorage()?.getItem(userStorageKey(RECOVERY_SESSION_KEY_PREFIX, userId)) === "1";
}

export function markPaywallRecoverySeenThisSession(userId: string): void {
  getSessionStorage()?.setItem(userStorageKey(RECOVERY_SESSION_KEY_PREFIX, userId), "1");
}

export function canShowUpgradeObjections(userId: string, now = Date.now()): boolean {
  const sessionStorage = getSessionStorage();
  if (sessionStorage?.getItem(userStorageKey(OBJECTION_SESSION_KEY_PREFIX, userId)) === "1") {
    return false;
  }

  const cooldownStartedAt = Number(
    getLocalStorage()?.getItem(userStorageKey(OBJECTION_COOLDOWN_KEY_PREFIX, userId))
  );
  return (
    !Number.isFinite(cooldownStartedAt) || now - cooldownStartedAt >= UPGRADE_OBJECTION_COOLDOWN_MS
  );
}

export function markUpgradeObjectionsShown(userId: string, now = Date.now()): void {
  getSessionStorage()?.setItem(userStorageKey(OBJECTION_SESSION_KEY_PREFIX, userId), "1");
  getLocalStorage()?.setItem(userStorageKey(OBJECTION_COOLDOWN_KEY_PREFIX, userId), String(now));
}
